// Statement.jsx - Updated to fetch accounts by lab ID first, then their statements
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ButtonGroup, Dropdown, Table, Form, Alert, Modal, Spinner } from "react-bootstrap";
import { FaCaretUp, FaCaretDown, FaPaperPlane, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import windowManager from "../../services/windowManager";

const Statement = () => {
    const [statements, setStatements] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const itemsPerPage = 10;
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
    const navigate = useNavigate();

    // State for checkboxes and email sending
    const [selectedStatements, setSelectedStatements] = useState([]);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [userStatementInfo, setUserStatementInfo] = useState(null);

    // New state to track if no lab is selected
    const [noLabSelected, setNoLabSelected] = useState(false);

    const handleEdit = (accountId, date) => {
        const formattedDate = formatDateForURL(date);
        navigate(`/edit-statement?accountIds=${accountId}&date=${formattedDate}`);
    };

    useEffect(() => {
        const fetchStatementsByLab = async () => {
            try {
                setLoading(true);
                setError(null);
                setNoLabSelected(false);

                // Get labId from localStorage
                const userLabId = localStorage.getItem('userLabId') || localStorage.getItem('labId');

                console.log('User Lab ID from localStorage:', userLabId);

                if (!userLabId) {
                    // Instead of throwing an error, set noLabSelected flag to true
                    setNoLabSelected(true);
                    setLoading(false);
                    return;
                }

                // Step 1: Fetch all accounts that belong to this lab using the lab-accounts API
                console.log('Fetching accounts for lab:', userLabId);
                const accountsResponse = await fetch(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                });

                if (!accountsResponse.ok) {
                    throw new Error('Failed to fetch accounts');
                }

                const accountsResult = await accountsResponse.json();
                console.log('Accounts found:', accountsResult.data);

                const accounts = accountsResult.data || [];

                if (accounts.length === 0) {
                    console.log('No accounts found for this lab');
                    setStatements([]);
                    setLoading(false);
                    return;
                }

                // Step 2: Extract account IDs from the accounts
                // Account ID could be either _id or accountId field
                const accountIds = accounts.map(account => account._id);
                const accountNumbers = accounts.map(account => account.accountId);

                console.log('Account ObjectIds:', accountIds);
                console.log('Account Numbers:', accountNumbers);

                // Step 3: Fetch statements for all these accounts
                // We'll fetch all statements and filter by account IDs
                const statementsResponse = await fetch('https://hms.backend.hiragroupcloud.com/api/statements/statements', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                });

                if (!statementsResponse.ok) {
                    throw new Error('Failed to fetch statements');
                }

                const statementsResult = await statementsResponse.json();
                const allStatements = statementsResult.data || [];

                console.log('Total statements fetched:', allStatements.length);

                // Step 4: Filter statements to only those belonging to the lab's accounts
                const filteredStatements = allStatements.filter(statement => {
                    // Check if statement's accountId matches any of the lab's account IDs
                    return accountIds.includes(statement.accountId) ||
                           accountNumbers.includes(statement.accountId) ||
                           accountIds.includes(statement.account?._id) ||
                           accountNumbers.includes(statement.account?.accountId);
                });

                console.log('Filtered statements count:', filteredStatements.length);
                console.log('Filtered statements:', filteredStatements);

                setStatements(filteredStatements);

                // Also fetch user statement email info
                await fetchUserStatementInfo();

            } catch (err) {
                setError(err.message);
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchStatementsByLab();
    }, []);

    // Fetch user statement email info
    const fetchUserStatementInfo = async () => {
        try {
            const response = await fetch('https://hms.backend.hiragroupcloud.com/api/statements/user-email', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUserStatementInfo(data);
            }
        } catch (err) {
            console.error('Error fetching user email info:', err);
        }
    };

    // Handle checkbox selection
    const handleCheckboxChange = (statement) => {
        const statementKey = `${statement.date || statement.runDate}-${statement.accountId}`;

        setSelectedStatements(prev => {
            const isSelected = prev.some(s =>
                (s.date === statement.date || s.date === statement.runDate) &&
                s.accountId === statement.accountId
            );

            if (isSelected) {
                return prev.filter(s =>
                    !((s.date === statement.date || s.date === statement.runDate) &&
                      s.accountId === statement.accountId)
                );
            } else {
                return [...prev, {
                    date: statement.date || statement.runDate,
                    accountId: statement.accountId,
                    accountName: statement.account || statement.accountHolder
                }];
            }
        });
    };

    // Handle select all checkboxes
    const handleSelectAll = () => {
        if (currentStatements.every(s => isStatementSelected(s))) {
            // If all are selected, deselect all
            setSelectedStatements(prev => prev.filter(s =>
                !currentStatements.some(cs =>
                    (cs.date === s.date || cs.date === s.runDate) &&
                    cs.accountId === s.accountId
                )
            ));
        } else {
            // Select all current page statements
            const newSelections = currentStatements
                .filter(s => !isStatementSelected(s))
                .map(s => ({
                    date: s.date || s.runDate,
                    accountId: s.accountId,
                    accountName: s.account || s.accountHolder
                }));
            setSelectedStatements(prev => [...prev, ...newSelections]);
        }
    };

    // Send selected statements via email - KEEP THIS VERSION (with userLabId)
    const handleSendStatements = async () => {
        try {
            setSending(true);
            setSendResult(null);

            console.log('========================================');
            console.log('📧 SEND STATEMENTS - FRONTEND');
            console.log('========================================');

            // Get active lab from localStorage
            const userLabId = localStorage.getItem('userLabId');

            console.log(
                '📧 userLabId from localStorage:',
                userLabId
            );

            console.log(
                '📧 currentTenant:',
                localStorage.getItem('currentTenant')
            );

            console.log(
                '📧 selectedStatements:',
                selectedStatements
            );

            // Make sure userLabId exists
            if (!userLabId) {
                throw new Error(
                    'No userLabId found in localStorage. Please select a lab and try again.'
                );
            }

            // Make sure statements are selected
            if (
                !selectedStatements ||
                !Array.isArray(selectedStatements) ||
                selectedStatements.length === 0
            ) {
                throw new Error(
                    'Please select at least one statement to send.'
                );
            }

            // IMPORTANT:
            // userLabId MUST be included in the request body.
            const payload = {
                userLabId: String(userLabId).trim(),

                selectedStatements: selectedStatements.map((s) => ({
                    date: s.date,
                    accountId: s.accountId
                }))
            };

            console.log('📧 ========================================');
            console.log('📧 FINAL REQUEST PAYLOAD');
            console.log('📧 ========================================');
            console.log(
                '📧 userLabId:',
                payload.userLabId
            );
            console.log(
                '📧 selectedStatements:',
                payload.selectedStatements
            );
            console.log(
                '📧 Complete payload:',
                payload
            );

            const token = localStorage.getItem('accessToken');

            if (!token) {
                throw new Error(
                    'Authentication token not found. Please log in again.'
                );
            }

            console.log(
                '📧 Access token exists:',
                !!token
            );

            const response = await fetch(
                'https://hms.backend.hiragroupcloud.com/api/statements/send-statements',
                {
                    method: 'POST',

                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },

                    body: JSON.stringify(payload)
                }
            );

            console.log(
                '📧 HTTP status:',
                response.status
            );

            const result = await response.json();

            console.log(
                '📧 Backend response:',
                result
            );

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    result.error ||
                    'Failed to send statements'
                );
            }

            setSendResult(result);
            setShowResultModal(true);

            // Clear selected statements only when all were sent successfully
            if (
                result.totalFailed === 0 &&
                result.totalSent > 0
            ) {
                setSelectedStatements([]);
            }

        } catch (err) {
            console.error(
                '❌ Error sending statements:',
                err
            );

            setSendResult({
                success: false,

                message:
                    'Error sending statements',

                error:
                    err.message ||
                    'Unknown error occurred',

                sent: [],

                failed: [
                    {
                        account: 'Error',
                        error:
                            err.message ||
                            'Unknown error occurred'
                    }
                ],

                totalSelected:
                    selectedStatements?.length || 0,

                totalSent: 0,

                totalFailed: 1
            });

            setShowResultModal(true);

        } finally {
            setSending(false);
            setShowConfirmModal(false);
        }
    };

    // Handle send button click
    const handleSendClick = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmKeyPress = (e) => {
        if (e.key === 'Enter' && !sending) {
            e.preventDefault();
            handleSendStatements();
        }
    };

    // Format currency values for display
    const formatCurrencyValue = (value) => {
        if (!value) return (0);
        if (typeof value === 'string') {
            const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
            return (isNaN(numericValue) ? 0 : numericValue);
        }
        return (value);
    };

    // Process statements to ensure proper currency formatting
    const processedStatements = useMemo(() => {
        return statements.map(statement => ({
            ...statement,
            oneTo30Days: formatCurrencyValue(statement.oneTo30Days),
            thirtyOneTo60Days: formatCurrencyValue(statement.thirtyOneTo60Days),
            sixtyOneTo90Days: formatCurrencyValue(statement.sixtyOneTo90Days),
            ninetyPlusDays: formatCurrencyValue(statement.ninetyPlusDays),
            newBalance: formatCurrencyValue(statement.newBalance)
        }));
    }, [statements]);

    const sortedStatements = useMemo(() => {
        let sortableItems = [...processedStatements];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key.includes('Days') || sortConfig.key === 'newBalance') {
                    aValue = parseFloat(aValue.replace(/[^\d.-]/g, '')) || 0;
                    bValue = parseFloat(bValue.replace(/[^\d.-]/g, '')) || 0;
                } else if (sortConfig.key === 'date') {
                    aValue = new Date(aValue).getTime();
                    bValue = new Date(bValue).getTime();
                } else if (sortConfig.key === 'account') {
                    aValue = (aValue || '').toString().toLowerCase();
                    bValue = (bValue || '').toString().toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [processedStatements, sortConfig]);

    const totalItems = sortedStatements.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const currentStatements = sortedStatements.slice(startIndex, endIndex);
    const showingFrom = startIndex + 1;
    const showingTo = Math.min(endIndex, totalItems);

    const handlePrevious = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };

    const formatDateForURL = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const openInNewWindow = (url) => {
        windowManager.openStatement(url);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcons = (key) => {
        return (
            <span style={{ marginLeft: '5px', display: 'inline-flex', flexDirection: 'column', verticalAlign: 'middle' }}>
                <FaCaretUp
                    style={{
                        color: sortConfig.key === key && sortConfig.direction === 'ascending' ? '#004aad' : 'black',
                        fontSize: '8px',
                        lineHeight: '4px',
                    }}
                />
                <FaCaretDown
                    style={{
                        color: sortConfig.key === key && sortConfig.direction === 'descending' ? '#004aad' : 'black',
                        fontSize: '8px',
                        lineHeight: '4px',
                    }}
                />
            </span>
        );
    };

    const isStatementSelected = (statement) => {
        return selectedStatements.some(s =>
            (s.date === statement.date || s.date === statement.runDate) &&
            s.accountId === statement.accountId
        );
    };

    return (
        <>
            <br />
            <div style={{ width: "100%", margin: "0", padding: "0 20px" }}>
                <div className="row" style={{ margin: 0 }}>
                    <div className="col-md-12" style={{ padding: 0 }}>
                        <h2>Statements</h2>

                        {/* Selection Info and Send Button */}
                        {selectedStatements.length > 0 && (
                            <div style={{
                                backgroundColor: "#e8f4fc",
                                padding: "10px 15px",
                                marginBottom: "15px",
                                borderLeft: "4px solid #004aad",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                                <div>
                                    <span style={{ fontWeight: "500", color: "#004aad" }}>
                                        <FaCheckCircle style={{ marginRight: "8px" }} />
                                        {selectedStatements.length} statement(s) selected
                                    </span>
                                    <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                                        {selectedStatements.slice(0, 3).map((s, i) => (
                                            <span key={i}>
                                                {s.accountName} ({formatDateDisplay(s.date)}){i < selectedStatements.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                        {selectedStatements.length > 3 && ` and ${selectedStatements.length - 3} more...`}
                                    </div>
                                </div>
                                <Button
                                    variant="success"
                                    onClick={handleSendClick}
                                    disabled={sending}
                                    style={{
                                        backgroundColor: "#28a745",
                                        borderColor: "#28a745",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px"
                                    }}
                                >
                                    {sending ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane />
                                            Send {selectedStatements.length} Statement(s)
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        <div id="postings_wrapper" className="dataTables_wrapper">
                            <div className="d-flex justify-content-between align-items-start" style={{ backgroundColor: "#e6e6e6", padding: "10px 15px", borderTop: "4px solid #004aad" }}>
                                <p style={{ margin: 0, color: "#333", fontWeight: "500" }}>
                                    Showing {showingFrom} to {showingTo} of {totalItems} entries
                                </p>

                                <div style={{ textAlign: "right" }}>
                                    <ButtonGroup>
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                style={{
                                                    backgroundColor: "#004aad",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    padding: "10px 14px",
                                                    fontWeight: "600",
                                                    fontSize: "14px"
                                                }}
                                            >
                                                Actions
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu style={{ borderRadius: "6px", boxShadow: "0 5px 12px rgba(0,0,0,0.15)", padding: "8px 0", minWidth: "180px" }}>
                                                <Dropdown.Item onClick={() => openInNewWindow("/runStatements")}>Run Statements</Dropdown.Item>
                                                <Dropdown.Item onClick={() => openInNewWindow("/reprint-statements")}>Reprint Statements</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </ButtonGroup>

                                    <div style={{ marginTop: "5px", display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                                        <button
                                            style={{
                                                backgroundColor: "#001f3f",
                                                border: "none",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                lineHeight: "20px",
                                                padding: 0,
                                                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                                opacity: currentPage === 1 ? 0.5 : 1
                                            }}
                                            onClick={handlePrevious}
                                            disabled={currentPage === 1}
                                        >
                                            ←
                                        </button>
                                        <button
                                            style={{
                                                backgroundColor: "#001f3f",
                                                border: "none",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                lineHeight: "20px",
                                                padding: 0,
                                                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                                                opacity: currentPage === totalPages ? 0.5 : 1
                                            }}
                                            onClick={handleNext}
                                            disabled={currentPage === totalPages}
                                        >
                                            →
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div style={{ textAlign: "center", padding: "40px" }}>
                                    <Spinner animation="border" variant="primary" />
                                    <p>Loading statements...</p>
                                </div>
                            ) : noLabSelected ? (
                                // New UI for when no lab is selected
                                <div style={{
                                    textAlign: "center",
                                    padding: "60px 20px",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    margin: "20px 0"
                                }}>
                                    <div style={{
                                        fontSize: "48px",
                                        marginBottom: "20px",
                                        color: "#6c757d"
                                    }}>
                                    </div>
                                    <h4 style={{
                                        color: "#495057",
                                        marginBottom: "15px",
                                        fontWeight: "500"
                                    }}>
                                        Please Select the Lab to view posting entries
                                    </h4>

                                </div>
                            ) : error ? (
                                <Alert variant="danger" style={{ margin: "20px" }}>
                                    <FaExclamationCircle style={{ marginRight: "8px" }} />
                                    Error: {error}
                                </Alert>
                            ) : (
                                <Table hover responsive style={{ borderCollapse: "collapse", width: "100%", marginBottom: 0 }}>
                                    <thead style={{ backgroundColor: "#e6e6e6", fontWeight: "500", color: "black" }}>
                                        <tr>
                                            <th style={{ backgroundColor: "#e6e6e6", width: "50px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={currentStatements.length > 0 &&
                                                        currentStatements.every(s => isStatementSelected(s))}
                                                    onChange={handleSelectAll}
                                                    style={{ margin: 0 }}
                                                />
                                            </th>
                                            <th style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('date')}>
                                                Date {getSortIcons('date')}
                                            </th>
                                            <th style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('account')}>
                                                Account {getSortIcons('account')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('oneTo30Days')}>
                                                1-30 Days {getSortIcons('oneTo30Days')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('thirtyOneTo60Days')}>
                                                31-60 Days {getSortIcons('thirtyOneTo60Days')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('sixtyOneTo90Days')}>
                                                61-90 Days {getSortIcons('sixtyOneTo90Days')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('ninetyPlusDays')}>
                                                90+ Days {getSortIcons('ninetyPlusDays')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('newBalance')}>
                                                New Balance {getSortIcons('newBalance')}
                                            </th>
                                            <th style={{ backgroundColor: "#e6e6e6" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {currentStatements.length > 0 ? (
                                        currentStatements.map((statement, index) => {
                                            const isSelected = isStatementSelected(statement);

                                            return (
                                                <tr key={`${statement.date || statement.runDate}-${statement.accountId}`}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleCheckboxChange(statement)}
                                                            style={{ margin: 0 }}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Link to={`/statements/by-date/${formatDateForURL(statement.date)}?accountIds=${statement.accountId}`} style={{ color: "#004aad", textDecoration: "none" }}>
                                                            {formatDateDisplay(statement.date)}
                                                        </Link>
                                                    </td>
                                                    <td>{statement.account}</td>
                                                    <td className="text-right">{statement.oneTo30Days}</td>
                                                    <td className="text-right">{statement.thirtyOneTo60Days}</td>
                                                    <td className="text-right">{statement.sixtyOneTo90Days}</td>
                                                    <td className="text-right">{statement.ninetyPlusDays}</td>
                                                    <td className="text-right">{statement.newBalance}</td>
                                                    <td>
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <Button
                                                                variant="primary"
                                                                style={{ backgroundColor: "#004aad", color: "white", borderColor: "#004aad" }}
                                                                onClick={() => openInNewWindow(`/run-date-range-statement?accountIds=${statement.accountId}&date=${formatDateForURL(statement.date)}`)}
                                                            >
                                                                Reprint
                                                            </Button>
                                                            <Button
                                                                variant="warning"
                                                                style={{ backgroundColor: "#004aad", color: "white", borderColor: "#004aad" }}
                                                                onClick={() => handleEdit(statement.accountId, statement.date)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="9" style={{ textAlign: "center", padding: "40px" }}>
                                                No statements found
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </Table>
                            )}
                            <div className="d-flex justify-content-between align-items-start" style={{ backgroundColor: "#e6e6e6", padding: "10px 15px", borderTop: "4px solid #004aad" }}>
                                <p style={{ margin: 0, color: "#333", fontWeight: "500" }}>
                                    Showing {showingFrom} to {showingTo} of {totalItems} entries
                                </p>

                                <div style={{ textAlign: "right" }}>
                                    <div style={{ marginTop: "5px", display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                                        <button
                                            style={{
                                                backgroundColor: "#001f3f",
                                                border: "none",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                lineHeight: "20px",
                                                padding: 0,
                                                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                                opacity: currentPage === 1 ? 0.5 : 1
                                            }}
                                            onClick={handlePrevious}
                                            disabled={currentPage === 1}
                                        >
                                            ←
                                        </button>
                                        <button
                                            style={{
                                                backgroundColor: "#001f3f",
                                                border: "none",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                lineHeight: "20px",
                                                padding: 0,
                                                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                                                opacity: currentPage === totalPages ? 0.5 : 1
                                            }}
                                            onClick={handleNext}
                                            disabled={currentPage === totalPages}
                                        >
                                            →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPaperPlane style={{ marginRight: "10px", color: "#28a745" }} />
                        Confirm Send Statements
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to send {selectedStatements.length} statement(s) to the respective account emails?</p>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '15px', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                        <h6 style={{ color: '#004aad', marginBottom: '10px' }}>Selected Statements:</h6>
                        <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                            {selectedStatements.map((statement, index) => (
                                <li key={index} style={{ padding: '5px 0', borderBottom: index < selectedStatements.length - 1 ? '1px dashed #eee' : 'none' }}>
                                    <strong>{statement.accountName}</strong> - {formatDateDisplay(statement.date)}
                                    <br />
                                    <small style={{ color: '#666' }}>Account ID: {statement.accountId}</small>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={sending}>
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleSendStatements}
                        onKeyPress={handleConfirmKeyPress}
                        disabled={sending}
                    >
                        {sending ? (
                            <>
                                <Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
                                Sending...
                            </>
                        ) : (
                            'Send Statements'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Result Modal */}
            <Modal show={showResultModal} onHide={() => setShowResultModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {sendResult?.totalFailed === 0 ? (
                            <FaCheckCircle style={{ marginRight: "10px", color: "#28a745" }} />
                        ) : (
                            <FaExclamationCircle style={{ marginRight: "10px", color: "#dc3545" }} />
                        )}
                        Send Results
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {sendResult && (
                        <div>
                            {sendResult.sent && sendResult.sent.length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h6 style={{ color: '#28a745', marginBottom: '10px' }}>
                                        Successfully Sent:
                                    </h6>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #d4edda', borderRadius: '4px', padding: '10px' }}>
                                        {sendResult.sent.map((item, index) => (
                                            <div key={index} style={{
                                                padding: '5px 0',
                                                borderBottom: index < sendResult.sent.length - 1 ? '1px dashed #c3e6cb' : 'none'
                                            }}>
                                                <strong>{item.account}</strong> - {formatDateDisplay(item.date)}
                                                <br />
                                                <small>Email: {item.email}</small>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {sendResult.failed && sendResult.failed.length > 0 && (
                                <div>
                                    <h6 style={{ color: '#dc3545', marginBottom: '10px' }}>
                                        <FaExclamationCircle style={{ marginRight: '5px' }} />
                                        Failed to Send:
                                    </h6>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #f8d7da', borderRadius: '4px', padding: '10px' }}>
                                        {sendResult.failed.map((item, index) => (
                                            <div key={index} style={{
                                                padding: '5px 0',
                                                borderBottom: index < sendResult.failed.length - 1 ? '1px dashed #f5c6cb' : 'none'
                                            }}>
                                                <strong>{item.account}</strong> - {item.error || 'Unknown error'}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowResultModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Statement;